import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';
import type { InboxItem } from '@shared/types';

export const useInboxStore = defineStore('inbox', () => {
  const items = ref<InboxItem[]>([]);
  const loading = ref(false);
  const filter = ref<'all' | 'unread'>('all');
  const query = ref('');
  const selectedId = ref<string | null>(null);

  const selected = computed(() => items.value.find((i) => i.email.id === selectedId.value) ?? null);

  const unreadCount = computed(
    () => items.value.filter((i) => !i.read_state || i.read_state.is_read === 0).length
  );

  async function load(opts: { reset?: boolean } = {}) {
    loading.value = true;
    try {
      const res = await api.get<InboxItem[]>('/inbox', {
        query: {
          filter: filter.value,
          q: query.value || undefined,
          limit: 100,
          t: Date.now(),
        },
      });
      if (res.ok && res.data) {
        items.value = opts.reset ? res.data : [...items.value, ...res.data];
      }
    } finally {
      loading.value = false;
    }
  }

  async function setRead(emailId: string, isRead: boolean) {
    // Optimistic
    const idx = items.value.findIndex((i) => i.email.id === emailId);
    if (idx >= 0) {
      const item = items.value[idx];
      items.value[idx] = {
        ...item,
        read_state: {
          user_id: item.read_state?.user_id ?? '',
          email_id: emailId,
          is_read: isRead ? 1 : 0,
          is_starred: item.read_state?.is_starred ?? 0,
          read_at: isRead ? Date.now() : null,
          starred_at: item.read_state?.starred_at ?? null,
        },
      };
    }
    try {
      await api.post(`/inbox/${emailId}/read`, { is_read: isRead });
    } catch {
      // rollback on error
      if (idx >= 0) {
        const item = items.value[idx];
        items.value[idx] = {
          ...item,
          read_state: {
            ...(item.read_state || ({} as any)),
            is_read: isRead ? 0 : 1,
          },
        };
      }
    }
  }

  function handleSocketNew(payload: any) {
    // For simplicity, we just reload. Real implementation would prepend an item.
    load({ reset: true });
  }

  function handleSocketDeleted(payload: { email_id: string }) {
    items.value = items.value.filter((i) => i.email.id !== payload.email_id);
    if (selectedId.value === payload.email_id) selectedId.value = null;
  }

  function handleSocketRestored(payload: { email_id: string }) {
    load({ reset: true });
  }

  function handleSocketRead(payload: { email_id: string; user_id: string; is_read: boolean }) {
    // Could affect badge counts, but client only tracks its own state
  }

  return {
    items,
    loading,
    filter,
    query,
    selectedId,
    selected,
    unreadCount,
    load,
    setRead,
    handleSocketNew,
    handleSocketDeleted,
    handleSocketRestored,
    handleSocketRead,
  };
});